import { useCallback, useContext, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import type {
  BenefitsTypes,
  Book,
  BookPages,
  Funnel,
  PageData,
  Sales,
  SalesInitializedProduct,
  SalesItem,
  SalesStaticItem,
  StaticComponentDataType
} from 'shared/types/index';
import type { SaleComponentsOrderItem, TypeOfPages } from '../context/SalesContext';
import {
  ApiService,
  baseKeysForPriceButtons,
  colorToKeyMapping,
  convertColorsToPallete,
  CURRENCIES,
  DefaultComponentAccesories,
  // areArraysEqual,
  generateUUID,
  getFileFromUrl,
  getImageDimensions,
  languageMapping,
  PRIMARY_CURRENCY_SYMBOLS,
  randomItem,
  removeStrings,
  TOP_USED_CURRENCIES,
  updateWebsiteColorsToKeys
} from 'shared/helpers/index';
import useSales from './useSales';
import { WebsiteCreationContext } from '../context';
import useBook from './useBook';
import useAuth from './useAuth';

import useBranding from './useBranding';
import slugify from 'slugify';
import moment from 'moment';
import { generateComponentWithFaker } from 'shared/helpers/openAiFaker';
import useOpenAi from './useOpenAi';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import useAlert from './useAlert';

const salesTypeToStaticMapping = {
  sales: 'sales_page',
  delivery: 'delivery_page'
};

const defaultFunnelType = 'digital_product';

type ComponentSetting = {
  key: string;
  version: string;
  static_alternation_id?: number;
  background_color?: string;
  default_image?: string;
};

type GenerationOptions = {
  newFunnelType?: string;
  defaultAi?: string;
};

type WebsiteGenerationParams = {
  newPrompt?: boolean;
  funnelType?: string;
  defaultAi?: string;
  imageMeta?: Record<string, Record<string, unknown>>;
  preserveExistingComponents?: boolean;
};

type GeneratedWebsiteData = {
  page_data: PageData;
  delivery_page_data: PageData;
};

type ComponentWithValues = {
  values: SalesStaticItem;
  component_name: string;
  version: number;
  background_color?: string;
};

/**
 * @function useWebsiteCreation
 *
 * @param {string} book_id - The book id
 *
 * @description This hook is used for all operations in the website creation process.
 * It uses the OpenAI hook to generate text and the Sales hook to manage the sales data.
 */
const useWebsiteCreation = () => {
  const { runAi } = useOpenAi();
  const { i18n, t } = useTranslation();

  const { isLoading, updateLoading } = useContext(WebsiteCreationContext);

  const { bookId } = useBook();
  const {
    webPrompt,
    ai: aiPrompt,
    updateWebPrompt,
    // price,
    salesPageValues,
    deliveryPageValues,
    websiteStaticData,
    type,
    updateInitialWebsite,
    salesPageId,
    updateAi,
    salesComponentOrdering,
    selectedCurrency,
    setFunnels,
    metaTags,
    setSalesPageCollection,
    salesType,
    thankYouType,
    price,
    subDomain,
    draft,
    salesExtraBody,
    isShippingForm,
    isSubscription,
    subscriptionData,
    setLastSaved,
    updatePendingChanges,
    upsellPrice,
    upsellDetails,
    funnelType,
    isDashboard,
    fpInfo
  } = useSales();
  const { bookName, bookMedia, setSalesData, salesData, updateSalesId } = useBook();
  const { user, userAvatar, isStaff, userLanguage, userLogo, userSetting } = useAuth();
  const { addAlert } = useAlert();

  const { colorPalette } = useBranding();

  const generateWebsiteValue = async (name: string, version: string = 'V1') => {
    const baseKey = name?.replace(/_\d+$/, '');

    const prompt = await generateAiBasedOnKey(
      {
        key: baseKey ?? '',
        version: version?.replace('V', '') ?? '1'
      },
      {
        newFunnelType: funnelType || defaultFunnelType,
        defaultAi: aiPrompt
      }
    );

    const data = type === 'sales' ? cloneDeep(salesPageValues) : cloneDeep(deliveryPageValues);
    const item = cloneDeep(data?.[name]);
    if (typeof item === 'object' && data) {
      const filled = fillExistingComponentValues(item, prompt);
      data[name] = filled;
      updateInitialWebsite(type, data);
    }
    return prompt;
  };

  const {
    isLoading: websiteSyncing,
    mutate: updateWebsite,
    mutateAsync: updateWebsiteAsync
  } = useMutation({
    mutationFn: ({ salesId, payload }: { salesId: number; payload: unknown }) =>
      ApiService.put(`/sales/sales-page/${salesId ?? salesPageId}/`, payload),
    onSuccess: (r) => {
      const modifiedDate = moment(r.data.modified_at).format('MMM Do, h:mm:ss a');
      setLastSaved(modifiedDate);
      updatePendingChanges(false);
      addAlert(t('utils.alerts.pagesSynced'), 'success');
    }
  });

  const salesPagePayload = useCallback(
    (additionalPayload: { salesPageValues?: PageData; deliveryPageValues?: PageData } = {}) => {
      const salesPageValuesParams = additionalPayload.salesPageValues ?? salesPageValues;
      const deliveryPageValuesParams = additionalPayload.deliveryPageValues ?? deliveryPageValues;

      if (!salesPageValuesParams || !deliveryPageValuesParams) return;

      const updatedWebsiteData = updateWebsiteColorsToKeys(salesPageValuesParams, colorPalette);
      const updatedDeliveryData = updateWebsiteColorsToKeys(deliveryPageValuesParams, colorPalette);

      const payload = {
        type: salesType === 'sales' ? 'sales' : 'lead_magnet',
        delivery_page: thankYouType,
        price: Number(price) || 0,
        price_currency: selectedCurrency,
        color_palette: colorPalette,
        page_data: updatedWebsiteData,
        delivery_page_data: updatedDeliveryData,
        funnel_type: funnelType,
        sub_domain: subDomain,
        book: bookId,
        is_draft: draft,
        sales_prompt: webPrompt,
        extra_body: salesExtraBody,
        extra_head: { ...metaTags, language: userLanguage },
        is_shipping_form: isShippingForm,
        is_subscription: isSubscription,
        ...(fpInfo && { account_id: fpInfo }),
        ...(upsellDetails?.product_name && {
          upsell_details: upsellDetails,
          price_2: Number(upsellPrice)
        }),
        ...(subscriptionData
          ? {
              subscription_info: {
                ...subscriptionData
              }
            }
          : {
              subscription_info: {
                monthly_price: 0,
                yearly_price: 0
              }
            }),
        ...additionalPayload
      };
      return payload;
    },
    [
      bookId,
      colorPalette,
      deliveryPageValues,
      draft,
      fpInfo,
      funnelType,
      isShippingForm,
      isSubscription,
      metaTags,
      price,
      salesExtraBody,
      salesPageValues,
      salesType,
      selectedCurrency,
      subDomain,
      subscriptionData,
      thankYouType,
      upsellDetails,
      upsellPrice,
      userLanguage,
      webPrompt
    ]
  );

  const handleDraftSavings = useCallback(
    (
      additionalPayload: { salesPageValues?: PageData; deliveryPageValues?: PageData } = {},
      params: { isAsync?: boolean; salesId?: number } = {
        isAsync: false,
        salesId: salesPageId
      }
    ) => {
      const payload = salesPagePayload(additionalPayload);
      if (params.isAsync) {
        updateWebsiteAsync({
          salesId: params.salesId || salesPageId,
          payload: payload
        });
      } else {
        updateWebsite({
          salesId: params.salesId || salesPageId,
          payload: payload
        });
      }
    },
    [salesPageId, salesPagePayload, updateWebsite, updateWebsiteAsync]
  );

  /**
   * Gets component ordering for a specific page type with fallback to digital_product
   */
  const getComponentOrdering = useCallback(
    (
      targetFunnelType: string,
      pageType: 'sales_page' | 'delivery_page'
    ): SaleComponentsOrderItem[] => {
      const actualComponents = salesComponentOrdering.find(
        (x) => x.funnel_name === targetFunnelType && x.sale_page_type === pageType
      )?.sale_component_order_items;

      const fallbackComponents = salesComponentOrdering.find(
        (x) => x.funnel_name === 'digital_product' && x.sale_page_type === pageType
      )?.sale_component_order_items;

      return actualComponents ?? fallbackComponents ?? [];
    },
    [salesComponentOrdering]
  );

  /**
   * Generates AI values for multiple components in parallel
   */
  const generateComponentsAiValues = async (
    components: SaleComponentsOrderItem[],
    targetFunnelType: string,
    defaultAi: string
  ): Promise<ComponentWithValues[]> => {
    if (components.length === 0) return [];

    try {
      const generatedValues = await Promise.all(
        components.map((component) =>
          generateAiBasedOnKey(
            {
              key: component?.component_name ?? '',
              version: String(component.version),
              static_alternation_id: component?.settings?.static_alternation_id
            },
            {
              newFunnelType: targetFunnelType,
              defaultAi: defaultAi
            }
          )
        )
      );

      return generatedValues.map((values, index) => ({
        values,
        component_name: components[index].component_name,
        version: components[index].version,
        background_color: components[index].settings?.background_color || ''
      }));
    } catch (error) {
      console.error('❌ Error generating AI values for components:', error);
      return [];
    }
  };

  /**
   * Creates component instances with unique IDs and proper ordering
   */
  const createComponentInstances = (
    componentsWithValues: ComponentWithValues[],
    imageMeta: Record<string, Record<string, unknown>> = {},
    aboutMeImage = ''
  ): Record<string, SalesItem> => {
    const idsContainer: Record<string, number> = {};

    return componentsWithValues
      .map((item, index) => {
        const { component_name, values, version, background_color } = item;

        // Track component instances for unique naming
        idsContainer[component_name] = (idsContainer[component_name] || 0) + 1;

        const componentId = `${component_name}_${idsContainer[component_name]}`;
        const extraMetadata = {
          ...(background_color ? { background_color } : {})
        };

        return generateComponentValue(
          component_name,
          values,
          version,
          index,
          componentId,
          undefined,
          aboutMeImage,
          imageMeta,
          extraMetadata
        );
      })
      .filter(Boolean) // Remove any undefined components
      .reduce(
        (acc, component) => {
          if (component?.id) {
            acc[component.id] = component;
          }
          return acc;
        },
        {} as Record<string, SalesItem>
      );
  };

  /**
   * Creates default empty PageData with required properties
   */
  const createEmptyPageData = (): PageData => ({
    base_color: 'color1',
    website_color: 'color2'
  });

  /**
   * Extracts existing components from page data to preserve structure
   */
  const extractExistingComponents = useCallback(
    (pageData: PageData | null | undefined): SaleComponentsOrderItem[] => {
      if (!pageData) return [];

      return Object.entries(pageData)
        .filter(
          ([key, value]) =>
            typeof value === 'object' &&
            value !== null &&
            !['base_color', 'website_color', 'secondary_color', 'pawprint', 'no_delivery'].includes(
              key
            )
        )
        .map(([key, component], index) => {
          // Extract component name and instance number
          const componentName = key.replace(/_\d+$/, '');
          const version = (component as SalesItem)?.version?.replace('V', '') || '1';

          return {
            id: Date.now() + index, // Generate unique ID for each component
            component_name: componentName,
            version: parseInt(version),
            order: (component as SalesItem)?.order || index + 1,
            settings: {}
          };
        })
        .sort((a, b) => a.order - b.order);
    },
    []
  );

  /**
   * Generates complete website values for both sales and delivery pages
   *
   * @param params - Generation parameters
   * @returns Promise<GeneratedWebsiteData> - Generated page data
   */
  const generateWebsiteValues = async (
    params: WebsiteGenerationParams = {}
  ): Promise<GeneratedWebsiteData> => {
    const {
      newPrompt = false,
      funnelType: targetFunnelType,
      defaultAi = bookName,
      imageMeta = {},
      preserveExistingComponents = false
    } = params;

    // Early return if no funnel type provided
    if (!targetFunnelType) {
      console.warn('⚠️ No funnel type provided for website generation');
      return {
        page_data: createEmptyPageData(),
        delivery_page_data: createEmptyPageData()
      };
    }

    updateLoading(true);

    try {
      // Update prompt if requested
      if (newPrompt) {
        updateWebPrompt(aiPrompt);
      }

      let salesComponents: SaleComponentsOrderItem[];
      let deliveryComponents: SaleComponentsOrderItem[];

      if (preserveExistingComponents) {
        // Extract existing components to preserve structure
        salesComponents = extractExistingComponents(salesPageValues);
        deliveryComponents = extractExistingComponents(deliveryPageValues);

        console.log(
          `🔄 Preserving existing structure with ${salesComponents.length} sales and ${deliveryComponents.length} delivery components`
        );
      } else {
        salesComponents = getComponentOrdering(targetFunnelType, 'sales_page');
        deliveryComponents = getComponentOrdering(targetFunnelType, 'delivery_page');

        console.log(
          `🏗️  Generating website with ${salesComponents.length} sales and ${deliveryComponents.length} delivery components`
        );
      }

      // Generate AI values for both page types in parallel
      const [salesComponentsWithValues, deliveryComponentsWithValues] = await Promise.all([
        generateComponentsAiValues(salesComponents, targetFunnelType, defaultAi),
        generateComponentsAiValues(deliveryComponents, targetFunnelType, defaultAi)
      ]);

      if (preserveExistingComponents) {
        // When preserving existing components, merge AI values with existing structure
        const existingSalesData = { ...salesPageValues };
        const existingDeliveryData = { ...deliveryPageValues };

        // Update existing components with new AI values
        salesComponentsWithValues.forEach((item) => {
          const componentKey = Object.keys(existingSalesData).find(
            (key) =>
              key.startsWith(item.component_name) && typeof existingSalesData[key] === 'object'
          );

          if (componentKey && existingSalesData[componentKey]) {
            existingSalesData[componentKey] = fillExistingComponentValues(
              existingSalesData[componentKey] as SalesItem,
              item.values
            );
          }
        });

        deliveryComponentsWithValues.forEach((item) => {
          const componentKey = Object.keys(existingDeliveryData).find(
            (key) =>
              key.startsWith(item.component_name) && typeof existingDeliveryData[key] === 'object'
          );

          if (componentKey && existingDeliveryData[componentKey]) {
            existingDeliveryData[componentKey] = fillExistingComponentValues(
              existingDeliveryData[componentKey] as SalesItem,
              item.values
            );
          }
        });

        console.log('✅ Successfully updated existing components with new AI values');

        return {
          page_data: existingSalesData as PageData,
          delivery_page_data: existingDeliveryData as PageData
        };
      } else {
        const salesPageComponents = createComponentInstances(salesComponentsWithValues, imageMeta);
        const deliveryPageComponents = createComponentInstances(
          deliveryComponentsWithValues,
          imageMeta
        );

        console.log(
          `✅ Successfully generated ${Object.keys(salesPageComponents).length} sales and ${Object.keys(deliveryPageComponents).length} delivery components`
        );

        return {
          page_data: { ...createEmptyPageData(), ...salesPageComponents },
          delivery_page_data: { ...createEmptyPageData(), ...deliveryPageComponents }
        };
      }
    } catch (error) {
      console.error('❌ Error in generateWebsiteValues:', error);

      // Return default data on error
      return {
        page_data: createEmptyPageData(),
        delivery_page_data: createEmptyPageData()
      };
    } finally {
      updateLoading(false);
    }
  };

  const defaultEmptyValues = (key: string) => {
    const sampleData = DefaultComponentAccesories[key];

    const defaultEmptyData = {
      ...(sampleData?.id && { id: generateUUID() }),
      ...(sampleData?.title && { title: '' }),
      ...(sampleData?.content && { content: '' }),
      ...(sampleData?.button && { button: '' }),
      ...(sampleData?.benefits &&
        sampleData.benefits.length > 0 && {
          benefits: sampleData.benefits.map((benefit) => {
            return {
              ...(benefit?.id && { id: generateUUID() }),
              ...(benefit.title && { title: '' }),
              ...(benefit.content && { content: '' }),
              ...(benefit.button && { button: '' })
            };
          })
        })
    };

    return cloneDeep(defaultEmptyData);
  };

  const getStaticValuesFirstVersion = (
    key: string,
    version: string = '1',
    newFunnelType = funnelType
  ) => {
    const staticType = salesTypeToStaticMapping[type];
    const DEFAULT_EMPTY_VALUES = defaultEmptyValues(key);

    const componentStaticValues = websiteStaticData[key];
    if (!componentStaticValues) return DEFAULT_EMPTY_VALUES;
    let productStaticvalues = componentStaticValues[Number(version)] || componentStaticValues[1];
    if (!productStaticvalues) {
      productStaticvalues = componentStaticValues[1];
    }
    if (!productStaticvalues) return DEFAULT_EMPTY_VALUES;
    const funnelStaticValues = productStaticvalues[newFunnelType];
    if (!funnelStaticValues) return DEFAULT_EMPTY_VALUES;
    const staticValues = funnelStaticValues[staticType];
    if (!staticValues) return DEFAULT_EMPTY_VALUES;
    const staticValue = randomItem(staticValues ?? []);

    return staticValue;
  };

  const getStaticValues = (
    setting: {
      key: string;
      version: string;
      static_alternation_id?: number;
      default_image?: string;
    },
    newFunnelType = funnelType
  ) => {
    const { key, static_alternation_id } = setting;
    const version = setting.version?.replace('V', '') || '1';
    const staticType = salesTypeToStaticMapping[type];
    const DEFAULT_EMPTY_VALUES = defaultEmptyValues(key);

    const componentStaticValues = websiteStaticData[key];
    if (!componentStaticValues) return DEFAULT_EMPTY_VALUES;
    let productStaticvalues = componentStaticValues[Number(version)] || componentStaticValues[1];
    if (!productStaticvalues) {
      productStaticvalues = componentStaticValues[1];
    }
    if (!productStaticvalues) return getStaticValuesFirstVersion(key, '1', newFunnelType);
    const funnelStaticValues = productStaticvalues[newFunnelType];
    if (!funnelStaticValues) return getStaticValuesFirstVersion(key, '1', newFunnelType);
    const staticValues = funnelStaticValues[staticType];
    if (!staticValues) return getStaticValuesFirstVersion(key, '1', newFunnelType);
    let staticValue = randomItem(staticValues ?? []);
    if (static_alternation_id && staticValues[static_alternation_id]) {
      staticValue = staticValues[static_alternation_id];
    }

    return staticValue;
  };

  const getPromptValues = async (
    key: string,
    _version: string = '1',
    newFunnelType = funnelType,
    defaultAi = bookName
  ) => {
    // const staticType = salesTypeToStaticMapping[type];
    const DEFAULT_EMPTY_VALUES = defaultEmptyValues(key);

    const aiValueOfWebsite = aiPrompt || webPrompt || bookName || defaultAi;
    if (localStorage.getItem('isAiMocked') === 'true') {
      return generateComponentWithFaker();
    }
    try {
      const response = await ApiService.post(`sales/sales-page/generate-ai-sales/`, {
        name: key,
        funnel_name: newFunnelType,
        sale_page_type: type === 'sales' ? 'sales_page' : 'delivery_page',
        prompt_input: aiValueOfWebsite,
        language: i18n.language || 'en',
        retry_count: 3
      });
      if (!response.data) return DEFAULT_EMPTY_VALUES;
      return response.data;
    } catch {
      return DEFAULT_EMPTY_VALUES;
    }
  };

  const replaceStaticVariables = (value?: string) => {
    if (!value) return '';

    const replacedValue = value
      .replaceAll('{PRODUCT.NAME}', bookName)
      .replaceAll('{USER.NAME}', user?.entity?.name || '')
      .replaceAll(
        '{NAME}',
        (() => {
          if (typeof user !== 'object' || user === null) return '';

          const entityName = user?.entity?.name;
          const businessName = user?.business_name;

          if (typeof entityName === 'string' && entityName.trim()) {
            return entityName.split(' ')[0]; // Use the first part of the entity name
          }

          if (typeof businessName === 'string' && businessName.trim()) {
            return businessName; // Use the business name if no entity name is found
          }

          return '';
        })()
      )
      .replaceAll('{USER.BUSINESS_NAME}', user?.business_name || '')
      .replaceAll('{PRODUCT.DESCRIPTION}', (metaTags?.['product_description'] as string) || '')
      .replaceAll('{PRODUCT.SPECIFICATION}', (metaTags?.['product_specification'] as string) || '');

    return replacedValue;
  };

  const fillComponentValues = async (
    key: string,
    promptValue: SalesStaticItem,
    staticValue: SalesStaticItem
  ) => {
    const value = defaultEmptyValues(key) as SalesStaticItem;
    promptValue = JSON.parse(JSON.stringify(promptValue).replaceAll('\n', ''));

    let titleValue = promptValue.title || replaceStaticVariables(staticValue.title);
    let contentValue = promptValue.content || replaceStaticVariables(staticValue.content);
    let buttonValue = promptValue.button || replaceStaticVariables(staticValue.button);

    const isTranslationNeeded = !['en', 'en-gb'].includes(i18n.language);

    if (!promptValue.title && titleValue && isTranslationNeeded) {
      titleValue = await runAi(
        `Translate This to ${titleValue}: ${languageMapping[i18n.language]}`,
        'Translation'
      );
    }
    if (!promptValue.content && contentValue && isTranslationNeeded) {
      contentValue = await runAi(
        `Translate This to ${contentValue}: ${languageMapping[i18n.language]}`,
        'Translation'
      );
    }
    if (!promptValue.button && buttonValue && isTranslationNeeded) {
      buttonValue = await runAi(
        `Translate This to ${buttonValue}: ${languageMapping[i18n.language]}`,
        'Translation'
      );
    }

    if (Object.prototype.hasOwnProperty.call(value, 'title') && titleValue) {
      value['title'] = titleValue;
    }
    if (Object.prototype.hasOwnProperty.call(value, 'content') && contentValue) {
      value['content'] = contentValue;
    }
    if (Object.prototype.hasOwnProperty.call(value, 'button') && buttonValue) {
      value['button'] = buttonValue;
    }
    if (Object.prototype.hasOwnProperty.call(value, 'benefits')) {
      value['benefits'] = value['benefits'] || [];
      if (promptValue?.benefits?.length && promptValue?.benefits?.length > 0) {
        value['benefits'] = staticValue?.benefits?.map((x, index) => {
          let copiedBenefit = { ...x };
          if (promptValue.benefits?.[index]?.title) {
            copiedBenefit = {
              ...copiedBenefit,
              title: promptValue.benefits?.[index]?.title
            };
          }
          if (promptValue.benefits?.[index]?.content) {
            copiedBenefit = {
              ...copiedBenefit,
              content: promptValue.benefits?.[index]?.content
            };
          }
          if (promptValue.benefits?.[index]?.button) {
            copiedBenefit = {
              ...copiedBenefit,
              button: promptValue.benefits?.[index]?.button
            };
          }
          return copiedBenefit;
        });
      }
      value.benefits?.forEach(async (benefit, index) => {
        const promptBenefit = promptValue.benefits?.[index];
        const staticBenefit = staticValue.benefits?.[index];

        let titleBenefit = promptBenefit?.title || replaceStaticVariables(staticBenefit?.title);
        let contentBenefit =
          promptBenefit?.content || replaceStaticVariables(staticBenefit?.content);
        let buttonBenefit = promptBenefit?.button || replaceStaticVariables(staticBenefit?.button);

        if (!promptValue?.benefits?.[index]?.title && titleBenefit && isTranslationNeeded) {
          titleBenefit = await runAi(
            `Translate This to ${titleBenefit}: ${languageMapping[i18n.language]}`,
            'Translation'
          );
        }
        if (!promptValue?.benefits?.[index]?.content && contentBenefit && isTranslationNeeded) {
          contentBenefit = await runAi(
            `Translate This to ${contentBenefit}: ${languageMapping[i18n.language]}`,
            'Translation'
          );
        }
        if (!promptValue?.benefits?.[index]?.button && buttonBenefit && isTranslationNeeded) {
          buttonBenefit = await runAi(
            `Translate This to ${buttonBenefit}: ${languageMapping[i18n.language]}`,
            'Translation'
          );
        }

        if (Object.prototype.hasOwnProperty.call(benefit, 'title') && titleBenefit) {
          benefit['title'] = titleBenefit;
        }
        if (Object.prototype.hasOwnProperty.call(benefit, 'content') && contentBenefit) {
          benefit['content'] = contentBenefit;
        }
        if (Object.prototype.hasOwnProperty.call(benefit, 'button') && buttonBenefit) {
          benefit['button'] = buttonBenefit;
        }
      });
    }

    return value;
  };

  const externalProductValues = (key: string, bullets: string[], benefitCount: number) => {
    const benefits = [] as Record<string, string>[];
    if (key.includes('check_section')) {
      let splitChar = '';
      const splitters = [':', '】', ']'];
      const splitterMap = {
        ':': ':',
        '】': '【',
        ']': '[',
        ')': '('
      } as Record<string, string>;
      splitters.forEach((splitter) => {
        if (bullets.every((bullet) => bullet.includes(splitter))) {
          splitChar = splitter;
        }
      });
      if (splitChar) {
        bullets.slice(0, benefitCount).forEach((bullet) => {
          const [t, c] = bullet.split(splitChar);

          benefits.push({
            title: t.replaceAll(splitChar, '').replaceAll(splitterMap[splitChar], '').trim(),
            content: c.replaceAll(splitChar, '').replaceAll(splitterMap[splitChar], '').trim()
          });
        });
      } else {
        bullets.slice(0, benefitCount).forEach((bullet) => {
          benefits.push({
            title: '',
            content: bullet
          });
        });
      }
    }
    return {
      benefits
    };
  };

  /**
   * Checks if Amazon product data should be used for component generation
   */
  const shouldUseAmazonProductData = useCallback(
    (key: string): boolean => {
      return (
        !!metaTags['amazon_url'] &&
        !!metaTags['amazon_feature_bullets'] &&
        Array.isArray(metaTags['amazon_feature_bullets']) &&
        metaTags['amazon_feature_bullets'].length > 0 &&
        key.includes('check_section')
      );
    },
    [metaTags]
  );

  /**
   * Logs debug information for staff users (excluding specific user)
   */
  const logDebugInfo = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, promptValues: any, staticValues: any) => {
      if (isStaff && !user?.email?.includes('platoni')) {
        console.group(`🔧 Component Generation Debug: ${key}`);
        console.log('📝 Prompt Values:', promptValues);
        console.log('🏗️  Static Values:', staticValues);
        console.log('🎯 Funnel Type:', funnelType);
        console.groupEnd();
      }
    },
    [isStaff, user?.email, funnelType]
  );

  /**
   * Generates AI-based component values with fallback to static values
   *
   * @param setting - Component configuration (key, version, alternation_id)
   * @param options - Generation options (funnel type, default AI prompt)
   * @returns Promise<SalesStaticItem> - Generated component values
   */
  const generateAiBasedOnKey = async (
    setting: ComponentSetting,
    options: GenerationOptions = {}
  ): Promise<SalesStaticItem> => {
    const { key, version } = setting;
    const { newFunnelType = funnelType, defaultAi = bookName } = options;

    try {
      // Get static and prompt values in parallel for better performance
      const staticValues = getStaticValues(setting, newFunnelType);
      const promptValues = await getPromptValues(
        key,
        version?.replace('V', ''),
        newFunnelType,
        defaultAi
      );

      let finalStaticValues = staticValues;
      let finalPromptValues = promptValues;

      // Handle Amazon product data override
      if (shouldUseAmazonProductData(key)) {
        const amazonValues = externalProductValues(
          key,
          metaTags['amazon_feature_bullets'] as string[],
          staticValues.benefits?.length ?? 0
        );
        finalStaticValues = amazonValues;
        finalPromptValues = amazonValues;
      }

      // Debug logging for development
      logDebugInfo(key, finalPromptValues, finalStaticValues);

      // Generate final component values
      return await fillComponentValues(key, finalPromptValues, finalStaticValues);
    } catch (error) {
      console.error(`❌ Error generating AI values for component "${key}":`, error);

      // Return safe fallback values on error
      const fallbackValues = getStaticValues(setting, newFunnelType);
      return await fillComponentValues(key, fallbackValues, fallbackValues);
    }
  };

  const fillExistingComponentValues = (component: SalesItem, values: SalesStaticItem) => {
    if (component?.title) {
      const defaultValue = typeof component?.title === 'string' ? component?.title : '';
      component['title'] = {
        ...component.title,
        val: values.title || defaultValue
      };
    }
    if (component?.content) {
      const defaultValue = typeof component?.['content'] === 'string' ? component?.['content'] : '';
      component['content'] = {
        ...component.content,
        val: values['content'] || defaultValue
      };
    }
    if (component?.button) {
      const defaultValue = typeof component?.button === 'string' ? component?.button : '';
      component['button'] = {
        ...component.button,
        val: values.button || defaultValue
      };
    }
    if (component?.benefits && component?.benefits?.length > 0) {
      const benefits = component.benefits;
      benefits.forEach((benefit, index) => {
        const defaultTitle = typeof benefit.title?.val === 'string' ? benefit?.title.val : '';
        const defaultContent = typeof benefit.content?.val === 'string' ? benefit.content.val : '';
        const defaultButton = typeof benefit.button?.val === 'string' ? benefit.button.val : '';

        const benefitsValue = values.benefits;

        if (benefit.title) {
          benefit['title'] = {
            ...component.title,
            val: benefitsValue?.[index]?.title || defaultTitle,
            id: generateUUID()
          };
        }
        if (!benefit.title && benefitsValue?.[index]?.title) {
          benefit['title'] = {
            ...component.title,
            val: benefitsValue?.[index]?.title,
            id: generateUUID()
          };
        }
        if (benefit.content) {
          benefit['content'] = {
            ...component.content,
            val: benefitsValue?.[index]?.content || defaultContent,
            id: generateUUID()
          };
        }
        if (!benefit.content && benefitsValue?.[index]?.content) {
          benefit['content'] = {
            ...component.content,
            val: benefitsValue?.[index]?.content,
            id: generateUUID()
          };
        }
        if (benefit.button) {
          benefit['button'] = {
            ...component.button,
            val: benefitsValue?.[index]?.button || defaultButton,
            id: generateUUID()
          };
        }
        if (!benefit.button && benefitsValue?.[index]?.button) {
          benefit['button'] = {
            ...component.content,
            val: benefitsValue?.[index]?.button || defaultButton,
            id: generateUUID()
          };
        }
      });
    }
    return component;
  };

  const categorizedColorPalette = useMemo(() => {
    return convertColorsToPallete(colorPalette);
  }, [colorPalette]);

  const SELECTOR_PATTERNS = useMemo(
    () => ({
      ABOUT_ME: /about_me/,
      HERO_VARIANTS: /^(hero_coach|hero_basic|hero)$/,
      HERO_GENERAL: /hero/,
      CHECK_SECTION: /check_section/,
      MINI_HERO: /mini_hero/
    }),
    []
  );

  const generateComponentValue = (
    selector: string,
    values: SalesStaticItem,
    version: number,
    order: number,
    componentId: string,
    type?: string,
    about_me_image?: string,
    image_meta: Record<string, Record<string, unknown>> = {},
    extraMetadata: Record<string, unknown> = {}
  ) => {
    const component = DefaultComponentAccesories[selector];
    if (!component) return;

    const generatedComponent = {
      id: componentId,
      version: `V${version}`,
      order: order,
      hidden: false,
      meta: extraMetadata
    } as SalesItem;

    if (type) {
      generatedComponent['meta'] = {
        [type]: type === 'url' ? '' : true
      };
    }

    if (!generatedComponent['id']) generatedComponent['id'] = generateUUID();

    if (SELECTOR_PATTERNS.ABOUT_ME.test(selector)) {
      const bookAboutMe = bookMedia?.find((m) => m.name.includes('photo'));
      const aboutMeImageUrl = about_me_image || bookAboutMe?.url || userAvatar?.file;
      generatedComponent['meta'] = {
        image: aboutMeImageUrl
      };
    }

    if (SELECTOR_PATTERNS.HERO_VARIANTS.test(selector) && image_meta['book_mockup']) {
      const value = image_meta['book_mockup'];
      generatedComponent['meta'] = {
        ...generatedComponent['meta'],
        ...value
      };
    }

    if (SELECTOR_PATTERNS.CHECK_SECTION.test(selector) && image_meta['hero_mockup']) {
      const value = image_meta['hero_mockup'];
      generatedComponent['meta'] = {
        ...generatedComponent['meta'],
        ...value
      };
    }

    if (SELECTOR_PATTERNS.MINI_HERO.test(selector) && image_meta['minihero_mockup']) {
      const value = image_meta['minihero_mockup'];
      generatedComponent['meta'] = {
        ...generatedComponent['meta'],
        ...value
      };
    }

    if (SELECTOR_PATTERNS.HERO_GENERAL.test(selector) && userAvatar?.file) {
      generatedComponent['meta'] = {
        ...generatedComponent['meta'],
        you_here: userAvatar?.file
      };
    }

    if (component.title) {
      generatedComponent['title'] = {
        id: generateUUID(),
        val: values.title || ''
      };
    }
    if (component.content) {
      generatedComponent['content'] = {
        id: generateUUID(),
        val: values.content || ''
      };
    }

    const lowerCaseColors = categorizedColorPalette?.mainColors.map((color) => ({
      ...color,
      value: color.value.toLowerCase()
    }));

    const index = lowerCaseColors.findIndex((c) => c.value === salesPageValues?.website_color);
    const secondary =
      lowerCaseColors.length === index + 1 ? lowerCaseColors[0] : lowerCaseColors[index + 1];
    const secondaryIndex = lowerCaseColors.findIndex((c) => c.value === secondary.value);
    const button =
      lowerCaseColors.length === secondaryIndex + 1
        ? lowerCaseColors[0]
        : lowerCaseColors[secondaryIndex + 1];

    if (component.button) {
      generatedComponent['button'] = {
        id: generateUUID(),
        val: values.button || '',
        color: button?.value || colorPalette.color3
      };
    }
    if (component.benefits && component.benefits.length > 0) {
      generatedComponent['benefits'] = values.benefits?.map((benefit) => {
        const benefitPart = {} as BenefitsTypes;

        if (benefit?.title || benefit?.title === '') {
          benefitPart['title'] = {
            id: generateUUID(),
            val: benefit?.title
          };
        }
        if (benefit?.content || benefit?.content === '') {
          benefitPart['content'] = {
            id: generateUUID(),
            val: benefit?.content
          };
        }
        if (benefit?.button || benefit?.button === '') {
          benefitPart['button'] = {
            id: generateUUID(),
            val: benefit?.button,
            color: colorPalette.color3
          };
        }

        return benefitPart;
      });
    }

    return generatedComponent;
  };

  const generateComponentValues = async (
    components: SaleComponentsOrderItem[],
    type: TypeOfPages = 'sales',
    newFunnelType: string,
    image_data: Record<string, Record<string, unknown>> = {}
  ) => {
    const componentDatas = (await Promise.allSettled(
      components.map(async (component) => {
        const { component_name, version } = component;
        return generateAiBasedOnKey(
          {
            key: component_name,
            version: String(version) || '1',
            static_alternation_id: component?.settings?.static_alternation_id
          },
          {
            newFunnelType: newFunnelType
          }
        );
      })
    )) as { status: string; value: SalesStaticItem }[];
    const idsContainer = {} as Record<string, number>;
    const componentValues = componentDatas.map((x) => x.value);
    const about_me_image = '';

    const generatedComponents = components
      .map((x, index) => {
        if (idsContainer[x.component_name]) {
          idsContainer[x.component_name] = idsContainer[x.component_name] + 1;
        } else {
          idsContainer[x.component_name] = 1;
        }
        const extraMeta = {
          ...(x?.settings?.background_color
            ? { background_color: x.settings.background_color }
            : {})
        };
        return generateComponentValue(
          x.component_name,
          componentValues[index],
          x.version,
          index + 1,
          `${x.component_name}_${idsContainer[x.component_name]}`,
          undefined,
          about_me_image,
          image_data,
          extraMeta
        );
      })
      .reduce((acc, curr) => {
        return { ...acc, [curr?.id as string]: curr };
      }, {});

    let copiedValues: PageData | null | undefined;

    if (type === 'sales') {
      copiedValues = cloneDeep(salesPageValues);

      Object.keys(salesPageValues ?? {}).forEach((x) => {
        const value = salesPageValues?.[x];
        if (typeof value === 'object') {
          delete copiedValues?.[x];
        }
      });
    } else {
      copiedValues = cloneDeep(deliveryPageValues);

      Object.keys(deliveryPageValues ?? {}).forEach((x) => {
        const value = deliveryPageValues?.[x];
        if (typeof value === 'object') {
          delete copiedValues?.[x];
        }
      });
    }

    const concatenatedValues = {
      ...copiedValues,
      ...generatedComponents
    } as PageData;
    updateInitialWebsite(type, concatenatedValues);
    return concatenatedValues;
  };

  const removePriceFromComponent = (value: string) => {
    let cleanedValue = value.replace(/\d+([,.]\d+)?/g, '').trim();
    cleanedValue = removeStrings(cleanedValue, Object.keys(TOP_USED_CURRENCIES));
    cleanedValue = removeStrings(cleanedValue, Object.values(TOP_USED_CURRENCIES));
    cleanedValue = cleanedValue.replaceAll('/m', '').replaceAll('/y', '');

    return cleanedValue;
  };

  const applyPriceToComponent = (value: string, price: number, currency: string) => {
    const currencySymbol = CURRENCIES[currency as keyof typeof CURRENCIES] || currency;
    let newValue = value;
    if (price > 0) {
      newValue = `${newValue} ${currencySymbol}${price}`;
      const primaryCurrencySelection =
        PRIMARY_CURRENCY_SYMBOLS[currencySymbol as keyof typeof PRIMARY_CURRENCY_SYMBOLS];
      if (primaryCurrencySelection && primaryCurrencySelection !== currency) {
        newValue = `${newValue} ${currency}`;
      }
    }
    return newValue;
  };

  const updatePriceForComponents = (
    initialData: PageData,
    price: number,
    currency: string = 'USD',
    isLocalUpdate: boolean = true
  ) => {
    let data = { ...initialData };
    Object.keys(data).forEach((key) => {
      if (!baseKeysForPriceButtons.some((base) => key.includes(base))) {
        return;
      }
      const copy = data[key];
      let payload = {};

      if (typeof copy === 'object') {
        const value = copy?.button?.val || '';
        const cleanedValue = removePriceFromComponent(value);
        const newValue = applyPriceToComponent(cleanedValue, price, currency);

        payload = {
          ...data,
          [key]: {
            ...copy,
            button: {
              ...copy.button,
              val: newValue
            }
          }
        };
        data = {
          ...data,
          ...payload
        };
      }
    });
    if (isLocalUpdate) {
      updateInitialWebsite(type, data);
    }
    return data;
  };

  const initializeDraftWebsite = async (
    book: Book,
    salesInitializedProduct: SalesInitializedProduct,
    extraPayload: Record<string, string | number> = {},
    extraSalesHeader: Record<string, unknown> = {},
    funnelType: string = 'digital_product'
  ) => {
    const customPage = book.pages?.find((p: BookPages) => p.name?.includes('AboutMe'));
    setSalesData({
      mockup: book.mockup,
      cover_thumbnail: book.cover_image_preview,
      pages_thumbnails: book.pages?.map((x: BookPages) => x.image_preview),
      about_me_image: customPage?.image_preview || '',
      color_palette: book.options.color_palette,
      book_file: book.book_file,
      book_name: book.name,
      type: 'sales',
      ...(book.sale_page && { id: book.sale_page })
    });

    // Return if sales page already exists, otherwise create a draft one
    if (salesData.id) return;
    updateAi(book.name);
    updateWebPrompt(book.name);

    const { salesPageData, deliveryPageData, extraHeader } = salesInitializedProduct;

    const pricingUpdates = updatePriceForComponents(
      salesPageData,
      Number(extraPayload?.['price']),
      selectedCurrency,
      false
    );

    let footer = user?.entity?.name;
    if (footer && user?.business_name) {
      footer = ` ${footer} & ${user?.business_name} `;
    }
    if (!footer) {
      footer = user?.business_name || user?.email;
    }
    const extra_body = {
      footer: ` ${footer} `,
      formButton: 'Sign Up to Get Started'
    };

    const payload = {
      type: 'lead_magnet',
      price: 0,
      price_currency: selectedCurrency,
      color_palette: book.options.color_palette,
      delivery_page: 'basic',
      funnel_type: funnelType,
      page_data: {
        ...pricingUpdates,
        website_color: 'color2',
        base_color: colorToKeyMapping('#000000', colorPalette),
        secondary_color: 'color3',
        pawprint: true
      },
      delivery_page_data: {
        ...deliveryPageData,
        base_color: colorToKeyMapping('#000000', colorPalette),
        website_color: 'color2',
        secondary_color: 'color3',
        ...(metaTags['amazon_url'] && { no_delivery: true })
      },
      extra_body,
      sub_domain: slugify(generateUUID(), { lower: true }),
      is_draft: true,
      sales_prompt: book.name,
      book: +book.id,
      extra_head: {
        ...extraHeader,
        ...extraSalesHeader
      },
      ...extraPayload
    };

    const response = await ApiService.post('/sales/sales-page/', payload);
    updateSalesId(response.data.id);
    const salesPage = await ApiService.get(`/sales/sales-page/${response.data.id}/`);
    return salesPage;
  };

  const addNewFunnelToStaffComponents = (data: StaticComponentDataType, newFunnelType: string) => {
    const dataCopy = { ...data };
    Object.keys(data || {}).forEach((key) => {
      const versionItem = data[key];
      Object.keys(versionItem).forEach((version) => {
        const funnelItem = versionItem[version];
        if (!funnelItem[newFunnelType]) {
          funnelItem[newFunnelType] = funnelItem['short_and_sweet'];
        }
        versionItem[version] = funnelItem;
      });
      dataCopy[key] = versionItem;
    });
    return dataCopy;
  };

  const { refetch: refetchSalesFunnel } = useQuery(
    `/sales/sale-funnel/?page_size=300`,
    () => ApiService.get(`/sales/sale-funnel/?page_size=300`),
    {
      select: (response) => response?.data?.results,
      onSuccess: (data) => {
        const sorted = data.sort((a: Funnel, b: Funnel) => a.id - b.id);
        setFunnels(sorted);
      },
      staleTime: 10 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!user?.user_id
    }
  );

  useQuery(`/sales/sales-page/`, () => ApiService.get(`/sales/sales-page/`), {
    select: (response) => response.data?.results as Sales[],
    onSuccess: (results) => {
      setSalesPageCollection(results);
    },
    refetchOnWindowFocus: false,
    retry: false,
    enabled: isDashboard,
    staleTime: 10 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });

  const handleExtraUserSettingsData = async (
    salesId: number,
    salesPageData?: PageData,
    deliveryPageData?: PageData
  ) => {
    const topbarLogoFile = userLogo?.file ? ((await getFileFromUrl(userLogo?.file)) as File) : null;
    const topbarLogoDimensions = topbarLogoFile
      ? ((await getImageDimensions(topbarLogoFile)) as {
          width: number;
          height: number;
        })
      : null;

    let topbar_1_extra_meta = {};
    if (topbarLogoFile && topbarLogoDimensions) {
      const formData = new FormData();
      formData.append('type', 'topbar_1');
      formData.append('file', topbarLogoFile);
      const response = await ApiService.post(`/sales/sales-page/${salesId}/media/`, formData);

      topbar_1_extra_meta = {
        width: topbarLogoDimensions?.width || 0,
        height: topbarLogoDimensions?.height || 0,
        d_width_1: topbarLogoDimensions?.width || 0,
        d_height_1: topbarLogoDimensions?.height || 0,
        x_1: 0,
        y_1: 0,
        image: response.data.file || ''
      };
    }

    const copiedSalespageValues = cloneDeep(salesPageData);
    if (
      !copiedSalespageValues?.['topbar_1'] ||
      typeof copiedSalespageValues['topbar_1'] !== 'object'
    )
      return;

    const topbarBenefits = cloneDeep(copiedSalespageValues['topbar_1'].benefits) || [];
    [1, 2, 3].forEach((index) => {
      const benefitIndex = index - 1;
      if (
        topbarBenefits[benefitIndex]?.title &&
        userSetting?.[`topbar_url_${index}` as keyof typeof userSetting] &&
        userSetting?.[`topbar_url_${index}_display_text` as keyof typeof userSetting]
      ) {
        topbarBenefits[benefitIndex].title = {
          ...topbarBenefits[benefitIndex].title,
          val: `<p><a href="${userSetting?.[`topbar_url_${index}` as keyof typeof userSetting]}" rel="noopener noreferrer" target="_blank">${userSetting?.[`topbar_url_${index}_display_text` as keyof typeof userSetting]}</a></p>`
        };
      }
    });

    copiedSalespageValues['topbar_1'] = {
      ...copiedSalespageValues['topbar_1'],
      meta: {
        ...copiedSalespageValues['topbar_1'].meta,
        ...topbar_1_extra_meta
      },
      benefits: topbarBenefits
    };

    await handleDraftSavings(
      {
        salesPageValues: copiedSalespageValues,
        deliveryPageValues: deliveryPageData
      },
      {
        isAsync: false,
        salesId
      }
    );
  };

  return {
    isLoading,
    updateLoading,
    generateWebsiteValues,
    generateAiBasedOnKey,
    fillExistingComponentValues,
    generateComponentValues,
    generateComponentValue,
    initializeDraftWebsite,
    addNewFunnelToStaffComponents,
    generateWebsiteValue,
    updatePriceForComponents,
    removePriceFromComponent,
    applyPriceToComponent,
    salesFunnelActions: {
      refetchSalesFunnel
    },
    salesPageActions: {
      updateWebsite,
      websiteSyncing
    },
    handleDraftSavings,
    handleExtraUserSettingsData
  };
};

export default useWebsiteCreation;
